/**
 * WorkshopDangerZone — the workshop detail right rail's disable / re-enable /
 * cancel controls.
 *
 *   - Active: "Disable workshop" only. A workshop has to stop selling before it
 *     can be cancelled.
 *   - Disabled: "Re-enable workshop", and "Cancel workshop" with an inline
 *     confirm that opens {@link CancelWorkshopFlow}.
 *   - Cancelled (or just cancelled): only the {@link CancelledBanner}.
 *
 * Rendered ONLY when the caller can edit (the rail gates this — Teacher never
 * sees it).
 */
import { useState } from 'react';

import type { WorkshopDetail } from '../../../../api/workshops';
import { DangerZonePanel } from '../../primitives';
import { useWorkshopSales } from '../use-workshop-sales';

import { CancelledBanner, type CancelledBannerDetail } from './CancelledBanner';
import { CancelWorkshopFlow } from './CancelWorkshopFlow';
import { CancelWorkshopRow } from './CancelWorkshopRow';
import { DisableWorkshopRow } from './DisableWorkshopRow';
import { ReEnableWorkshopRow } from './ReEnableWorkshopRow';

export interface WorkshopDangerZoneProps {
  workshop: WorkshopDetail;
}

type Stage = 'idle' | 'confirm' | 'flow';

export function WorkshopDangerZone({ workshop }: WorkshopDangerZoneProps) {
  const [stage, setStage] = useState<Stage>('idle');
  const [cancelled, setCancelled] = useState(false);
  const [banner, setBanner] = useState<CancelledBannerDetail | null>(null);
  // Owned here, not by the rows, so a refused transition's reason survives the
  // swap between the disable and re-enable rows when the page refetches.
  const sales = useWorkshopSales(workshop.id);

  if (workshop.lifecycle === 'cancelled' || cancelled) {
    return <CancelledBanner detail={banner} />;
  }

  return (
    <>
      <DangerZonePanel>
        {workshop.lifecycle === 'disabled' ? (
          <>
            <ReEnableWorkshopRow sales={sales} />
            <CancelWorkshopRow
              confirming={stage !== 'idle'}
              onStart={() => setStage('confirm')}
              onContinue={() => setStage('flow')}
              onCancel={() => setStage('idle')}
            />
          </>
        ) : (
          <DisableWorkshopRow sales={sales} />
        )}
      </DangerZonePanel>

      {stage === 'flow' && (
        <CancelWorkshopFlow
          workshop={workshop}
          onClose={() => setStage('idle')}
          onCancelled={(detail) => {
            setBanner(detail);
            setCancelled(true);
          }}
        />
      )}
    </>
  );
}
